import { Component, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BackendApi } from '../../services/backend-api';
import { EXCEPTION_TYPES, UsageRateArea, UsageRateExtended, UsageRateZone } from '../../models/usage-rate';
import { AgGridAngular } from 'ag-grid-angular';
import type { AutoSizeStrategy, CellEditRequestEvent, ColDef, GetRowIdParams, GridApi, GridReadyEvent } from "ag-grid-community";
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import {MatExpansionModule} from '@angular/material/expansion';
import { CreateUsageRateAreaDialog } from '../create-usage-rate-area-dialog/create-usage-rate-area-dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CreateUsageRateZoneDialog } from '../create-usage-rate-zone-dialog/create-usage-rate-zone-dialog';
import { IconButtonCellRenderer } from '../icon-button-cell-renderer/icon-button-cell-renderer';
import { ConfirmationDialog } from '../confirmation-dialog/confirmation-dialog';
import { DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { UpdateUsageRateDialog } from '../update-usage-rate-dialog/update-usage-rate-dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-show-usage-rate',
  providers: [DecimalPipe],
  imports: [AgGridAngular, MatExpansionModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './show-usage-rate.html',
  styleUrl: './show-usage-rate.css',
})
export class ShowUsageRate {
  private _activatedRoute = inject(ActivatedRoute);
  private _snackBar = inject(MatSnackBar);
  private _decimalPipe = inject(DecimalPipe);
  private _router = inject(Router);

  readonly exceptionTypes = EXCEPTION_TYPES;

  public usageRate = signal<UsageRateExtended|undefined>(undefined);

  public autoSizeStrategy: AutoSizeStrategy = {
    type: 'fitGridWidth',
    defaultMinWidth: 100,
  };

  public localeText = AG_GRID_LOCALE_FR;

  public areaColumns: ColDef<UsageRateArea>[] = [
    { field: "area.country.flag", headerName: "", filter: false, sortable: false, width: 50 },
    { field: "area.country.name_fr", headerName: "Pays", filter: true, sortable: true, floatingFilter: true },
    { field: "area.is_landline", cellDataType: false, headerName: "Fixe / Mobile", valueFormatter: (params) => params.value ? 'Fixe' : 'Mobile' },
    { field: "price", headerName: "Tarif (€)", type: 'numericColumn', width: 100, valueFormatter: (params) => this.formatCurrency(params.value), editable: true, cellEditor: 'agNumberCellEditor' },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette entrée',
        onClick: (data: any, index: number) => this.deleteUsageRateArea(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  public zoneColumns: ColDef<UsageRateZone>[] = [
    { field: "zone", headerName: "Zone", filter: true, sortable: true, floatingFilter: true },
    { field: "price", headerName: "Tarif (€)", type: 'numericColumn', width: 100, valueFormatter: (params) => this.formatCurrency(params.value), editable: true, cellEditor: 'agNumberCellEditor' },
    {
      cellRenderer: IconButtonCellRenderer,
      cellRendererParams: {
        icon: 'delete',
        tooltip: 'Supprimer cette entrée',
        onClick: (data: any, index: number) => this.deleteUsageRateZone(data, index)
      },
      filter: false,
      sortable: false,
      suppressHeaderMenuButton: true,
      width: 50
    }
  ];

  private areaGridApi!: GridApi;
  private zoneGridApi!: GridApi;

  formatCurrency(value: number): string {
    return this._decimalPipe.transform(value, "1.4-4") ?? "";
  }

  constructor(
    private readonly backendApi: BackendApi,
    private readonly dialog: MatDialog
  ) {
    this._activatedRoute.params.subscribe((params) => {
      this.backendApi.getUsageRate(params['uuid']).subscribe({
        next: (usageRate) => {
          this.usageRate.set(usageRate);
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la recuperation de la grille de vente.");
        }
      })
    });
  }

  onAreaGridReady(event: GridReadyEvent) {
    this.areaGridApi = event.api;
  }

  onZoneGridReady(event: GridReadyEvent) {
    this.zoneGridApi = event.api;
  }

  getRowId(params: GetRowIdParams): string {
    return params.data.uuid;
  }

  createUsageRateArea() {
    const dialogRef = this.dialog.open(CreateUsageRateAreaDialog, {
      data: this.usageRate(),
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((usageRateArea) => {
      if (usageRateArea) {
        this.areaGridApi.applyTransaction({
          add: [usageRateArea]
        });
      }
    });
  }

  createUsageRateZone() {
    const dialogRef = this.dialog.open(CreateUsageRateZoneDialog, {
      data: this.usageRate(),
      width: '400px'
    });

    dialogRef.afterClosed().subscribe((usageRateZone) => {
      if (usageRateZone) {
        this.zoneGridApi.applyTransaction({
          add: [usageRateZone]
        });
      }
    });
  }

  deleteUsageRateArea(usageRateArea: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette entrée ?"},
    });
  
    dialogRef.afterClosed().subscribe(result => {
      const usageRateUuid = this.usageRate()?.uuid;

      if (!result || !usageRateUuid) {
        return;
      }

      this.backendApi.deleteUsageRateArea(usageRateUuid, usageRateArea.uuid).subscribe({
        next: () => {
          this.areaGridApi.applyTransaction({remove: [usageRateArea]});
          this._snackBar.open("Entrée supprimée.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }

  deleteUsageRateZone(usageRateZone: any, index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette entrée ?"},
    });
  
    dialogRef.afterClosed().subscribe(result => {
      const usageRateUuid = this.usageRate()?.uuid;

      if (!result || !usageRateUuid) {
        return;
      }

      this.backendApi.deleteUsageRateZone(usageRateUuid, usageRateZone.uuid).subscribe({
        next: () => {
          this.zoneGridApi.applyTransaction({remove: [usageRateZone]});
          this._snackBar.open("Entrée supprimée.");
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }

  onAreaCellEditRequest(event: CellEditRequestEvent) {
    const usageRateUuid = this.usageRate()?.uuid;

    if (!usageRateUuid || event.colDef.field != "price" || event.oldValue == event.newValue) {
      return;
    }

    this.backendApi.patchUsageRateArea(usageRateUuid, event.data.uuid, {price: event.newValue}).subscribe({
      next: (response) => {
        event.node.setData(response);
        //this.zoneGridApi.applyTransaction({update: [response]});
        this._snackBar.open("Tarif modifié.");
      },
      error: () => {
        this._snackBar.open("Prix modifié.");
        this._snackBar.open("Erreur rencontrée lors de la modification.");
      }
    })
  }

  onZoneCellEditRequest(event: CellEditRequestEvent) {
    const usageRateUuid = this.usageRate()?.uuid;

    if (!usageRateUuid || event.colDef.field != "price" || event.oldValue == event.newValue) {
      return;
    }

    this.backendApi.patchUsageRateZone(usageRateUuid, event.data.uuid, {price: event.newValue}).subscribe({
      next: (response) => {
        event.node.setData(response);
        //this.areaGridApi.applyTransaction({update: [response]});
        this._snackBar.open("Tarif modifié.");
      },
      error: () => {
        this._snackBar.open("Erreur rencontrée lors de la modification.");
      }
    })
  }

  updateUsageRate() {
    if (!this.usageRate()) {
      return;
    }

    const dialogRef = this.dialog.open(UpdateUsageRateDialog, {
      width: '400px',
      data: this.usageRate()
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.usageRate.set(result);
    });
  }

  deleteUsageRate(): void {
    const usageRateUuid = this.usageRate()?.uuid;

    if (!usageRateUuid) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {content: "Êtes-vous sûr de vouloir supprimer cette grille de vente ?"},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      this.backendApi.deleteUsageRate(usageRateUuid).subscribe({
        next: () => {
          this._snackBar.open("Grille de vente supprimée.");
          this._router.navigate(["/usageRates"]);
        },
        error: () => {
          this._snackBar.open("Erreur rencontrée lors de la suppression.");
        }
      })
    });
  }

  getExceptionTypeLabel() {
    const usageRateType = this.usageRate()?.type;

    if (!usageRateType || !(usageRateType in EXCEPTION_TYPES)) {
      return '';
    }

    return EXCEPTION_TYPES[usageRateType];
  }
}
